import { useEffect, useRef } from 'react'

const STATS = [
  {
    count: 3,
    small: 'sec',
    suffix: '',
    lbl: 'is the entire UPI window between initiation and settlement. Anything slower stops being prevention and starts being recovery.',
  },
  {
    count: 78,
    small: '%',
    suffix: '',
    lbl: 'of money lost to instant-rail fraud never comes back once funds clear. Post-settlement recovery is the exception, not the rule.',
  },
  {
    count: 0,
    small: 'days',
    suffix: '',
    lbl: 'head-start when a new typology surfaces. Fraud rings weaponise fresh patterns within minutes of discovery.',
  },
]

function CountUp({ to }) {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    let raf = 0
    const io = new IntersectionObserver((es) => {
      es.forEach((e) => {
        if (!e.isIntersecting) return
        const duration = 1.6
        const damping = 20 + 40 * (1 / duration)
        const stiffness = 100 * (1 / duration)
        let p = 0, v = 0, prev = performance.now(), settled = 0
        cancelAnimationFrame(raf)
        const step = (now) => {
          const dt = Math.min(0.064, (now - prev) / 1000)
          prev = now
          const force = stiffness * (to - p) - damping * v
          v += force * dt
          p += v * dt
          el.textContent = Math.round(p).toString()
          if (Math.abs(to - p) < 0.01 && Math.abs(v) < 0.01) {
            if (++settled > 2) { el.textContent = to.toString(); return }
          } else settled = 0
          raf = requestAnimationFrame(step)
        }
        raf = requestAnimationFrame(step)
      })
    })
    io.observe(el)
    return () => { cancelAnimationFrame(raf); io.disconnect() }
  }, [to])
  return <span ref={ref} className="sf-num-end">0</span>
}

export default function InterceptSection() {
  const secRef = useRef(null)

  useEffect(() => {
    const sec = secRef.current
    if (!sec) return
    const stats = sec.querySelectorAll('.sf-problem-stat')
    const onMove = (e) => {
      const r = sec.getBoundingClientRect()
      const cx = (e.clientX - r.left) / r.width - 0.5
      const cy = (e.clientY - r.top) / r.height - 0.5
      stats.forEach((s, i) => {
        const depth = (i - 1) * 8
        s.style.transform = `translate3d(${cx * depth}px, ${cy * depth}px, 0)`
      })
    }
    const onLeave = () => stats.forEach((s) => { s.style.transform = '' })
    sec.addEventListener('mousemove', onMove)
    sec.addEventListener('mouseleave', onLeave)
    return () => {
      sec.removeEventListener('mousemove', onMove)
      sec.removeEventListener('mouseleave', onLeave)
    }
  }, [])

  return (
    <section ref={secRef} className="sf-problem" id="problem">
      <div className="sf-container">
        <div className="sf-problem-headline sf-reveal" data-sf-reveal>
          <span className="sf-eyebrow"><span className="sf-pulse" />On the ground today</span>
          <h2 className="sf-problem-h2">
            Money moves in seconds. <span className="sf-accent">So does fraud.</span>
          </h2>
        </div>
        <div className="sf-problem-stats">
          {STATS.map((s, i) => (
            <div
              key={i}
              className={`sf-problem-stat sf-reveal${i ? ` sf-reveal-d${i}` : ''}`}
              data-sf-reveal
            >
              <div className="sf-problem-num">
                <CountUp to={s.count} />
                <span className="sf-problem-small">{s.small}</span>
              </div>
              <div className="sf-problem-lbl">{s.lbl}</div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .sf-problem {
          position: relative; background: var(--bg);
          padding: clamp(96px,11vw,150px) 0;
        }
        .sf-problem-headline { max-width: 920px; margin: 0 auto; text-align: center; }
        .sf-problem-h2 {
          margin-top: 24px;
          font-size: clamp(2rem,4.5vw,3.8rem);
          font-weight: 500; letter-spacing: -0.028em;
          line-height: 1.15; color: var(--text); text-wrap: balance;
        }
        .sf-accent { color: var(--ca-600); font-weight: 600; position: relative; }
        .sf-accent::after {
          content: ''; position: absolute; inset: auto 0 -2px 0; height: 3px;
          background: linear-gradient(to right,var(--ca-500),var(--sf-500));
          transform: scaleX(0); transform-origin: 0% 50%;
          transition: transform 1.2s var(--ease);
        }
        :global(.sf-problem-headline.sf-in) .sf-accent::after { transform: scaleX(1); }
        .sf-problem-stats {
          margin-top: 80px;
          display: grid; grid-template-columns: repeat(3,1fr);
          gap: 32px; max-width: 880px;
          margin-left: auto; margin-right: auto;
        }
        @media (max-width: 760px) {
          .sf-problem-stats { grid-template-columns: 1fr; gap: 24px; }
        }
        .sf-problem-stat { text-align: center; transition: transform 0.4s var(--ease); }
        .sf-problem-num {
          font-size: clamp(3rem,6vw,5rem);
          font-weight: 600; letter-spacing: -0.03em; line-height: 1;
          background: linear-gradient(135deg,var(--ca-600),var(--ca-cyan));
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }
        .sf-problem-small {
          font-size: 0.5em; margin-left: 2px; vertical-align: top;
          background: inherit; -webkit-background-clip: inherit; background-clip: inherit; color: inherit;
        }
        .sf-problem-lbl {
          margin-top: 14px; font-size: 15px;
          color: var(--text-2); line-height: 1.55;
        }
      `}</style>
    </section>
  )
}
